import React, { useEffect, useMemo, useState } from 'react';
import { DeliveriesTable } from './components';
import { CustomerDelivery, DeliveryStatus } from './deliveriesTypes';
import { listMyCustomerDeliveries } from '../../../services/deliveriesApi';
import { DeliveryStop, StopStatus } from '../../../services/deliveriesApi';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CustomerDeliveries.module.css';

const statusMap: Record<StopStatus, DeliveryStatus> = {
  pending: 'Scheduled',
  in_transit: 'In Transit',
  delivered: 'Completed',
  failed: 'Failed',
  skipped: 'Failed',
};

function formatEta(stop: DeliveryStop): string {
  if (stop.status === 'delivered' && stop.completed_at) {
    return new Date(stop.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  if (stop.scheduled_window_start) {
    const d = new Date(stop.scheduled_window_start);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return isToday ? `Today, ${time}` : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`;
  }
  return '—';
}

function adapt(stop: DeliveryStop): CustomerDelivery {
  return {
    id: String(stop.id),
    deliveryId: `DEL-${String(stop.id).padStart(4, '0')}`,
    orderNumber: formatOrderId(stop.order_id),
    driverName: '—',
    eta: formatEta(stop),
    status: statusMap[stop.status],
  };
}

const CustomerDeliveries: React.FC = () => {
  const [stops, setStops] = useState<DeliveryStop[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    listMyCustomerDeliveries({ limit: 50 })
      .then((res) => setStops(res ?? []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load deliveries'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const adapted = useMemo<CustomerDelivery[]>(() => stops.map(adapt), [stops]);

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={loading && stops.length === 0}
        error={error}
        empty={!loading && adapted.length === 0}
        emptyMessage="No deliveries yet"
        onRetry={load}
      >
        <DeliveriesTable deliveries={adapted} />
      </AsyncBoundary>
    </div>
  );
};

export const CustomerDeliveriesPage: React.FC = () => {
  return <CustomerDeliveries />;
};

export { CustomerDeliveries };
export default CustomerDeliveries;
